import { Store } from '@ngrx/store';
import { includes } from 'lodash';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import {
  AccessibleAirCarrierDto,
  CrewRole,
  UserRole,
} from 'src/app/shared/flight-log-api/shared/shared-client.model';
import { AppState } from 'src/store';
import { BaseEntity } from 'src/store/base/base.model';
import { CompanyEntity } from 'src/store/company/company.model';
import { selectShiftsByStartTimeDesc } from 'src/store/duty-log/duty-log.selectors';
import { EmployeeEntity } from 'src/store/employee/employee.model';
import { UserProps_Deprecated } from 'src/util/interfaces';
import { DutyLogEntryViewModel } from './duty-log-entry-view-model';

export const dutyLogCompanyBlacklist: string[] = [];

export interface FlightLogFeatureProps {
  advantages: boolean;
  flightTagNumber: boolean;
  perLegLogging: boolean;
  perFlightLogging: boolean;
  part135part91: boolean;
  part133: boolean;
  approachDepartures: boolean;
  ifrPracticeOnly: boolean;
  crewMemberHnvgo: boolean;
  startingHobbsReadonly: boolean;
  totalsMaintenanceSummary: boolean;
  validatePatientOnboard: boolean;
  dutyLogManagementForUser: boolean;
  dutyLogmanagementForCompany: boolean;
}

export class FlightLogFeatures implements FlightLogFeatureProps {
  public advantages: boolean;
  public flightTagNumber: boolean;
  public perLegLogging: boolean;
  public perFlightLogging: boolean;
  public part135part91: boolean;
  public part133: boolean;
  public approachDepartures: boolean;
  public ifrPracticeOnly: boolean;
  public crewMemberHnvgo: boolean;
  public startingHobbsReadonly: boolean;
  public totalsMaintenanceSummary: boolean;
  public validatePatientOnboard: boolean;
  public tcsUnitManagement: boolean;
  public dutyLogManagementForUser: boolean;
  public showSidebarGoldenHourLink: boolean;
  public dutyLogmanagementForCompany: boolean;

  // tslint:disable-next-line: variable-name
  constructor(private _user: UserViewModel) {
    const disableFor = (...companyCodes: string[]) => !includes(companyCodes, _user.companycode);
    const enableFor = (...companyCodes: string[]) => includes(companyCodes, _user.companycode);
    this.advantages = disableFor('MTC', 'AEL', 'EGM');
    this.flightTagNumber = disableFor('MTC', 'AEL', 'EGM');
    this.perLegLogging = disableFor('MTC', 'AEL', 'EGM', 'GFL');
    this.perFlightLogging = disableFor('RCH');
    this.part135part91 = disableFor('AEL');
    this.approachDepartures = disableFor('AEL');
    this.ifrPracticeOnly = enableFor('AEL');
    this.crewMemberHnvgo = disableFor('RCH');
    this.startingHobbsReadonly = enableFor('MTC', 'EGM');
    this.totalsMaintenanceSummary = enableFor('MTC', 'EGM', 'GFL');
    this.validatePatientOnboard = enableFor('RCH');
    this.tcsUnitManagement = enableFor('MTC', 'AEL', 'EGM', 'RCH');
    this.showSidebarGoldenHourLink = enableFor(...dutyLogCompanyBlacklist);
    this.dutyLogmanagementForCompany = disableFor(...dutyLogCompanyBlacklist);
    this.dutyLogManagementForUser =
      this.dutyLogmanagementForCompany ||
      (this._user.base != null && this._user.base.enableFlightLogDutyLog);
  }

  get editDutyHistory(): boolean {
    if (this._user.isAviationSupervisor) {
      return true;
    }

    return (
      this._user.companycode !== 'MTC' &&
      this._user.companycode !== 'EGM' &&
      this._user.companycode !== 'AEL'
    );
  }
  public isFlighTimeRequired(companyCode?: string): boolean {
    return companyCode !== 'GFL';
  }
  get deleteDutyHistory(): boolean {
    return this._user.isAviationSupervisor;
  }
}

export class EmployeeViewModel implements EmployeeEntity {
  public companyId: string;
  public baseId?: string | undefined;
  public reportsToEmployeeId: string;
  public departmentId: string;
  public role: CrewRole;
  public email: string;
  public title: string;
  public userRoles: UserRole[];
  public accessibleAirCarriers: AccessibleAirCarrierDto[];
  public readonly employee: EmployeeEntity;
  public readonly companycode: string;
  constructor(employee: EmployeeEntity) {
    this.employee = employee;
    this.companyId = employee.companyId;
    this.baseId = employee.baseId;
    this.departmentId = employee.departmentId;
    this.role = employee.role;
    this.title = employee.title;
    this.email = employee.email;
    this.userRoles = employee.userRoles ?? [];
    this.accessibleAirCarriers = employee.accessibleAirCarriers ?? [];
    this.companycode = this.employee.companyId === 'CSA' ? 'RCH' : this.employee.companyId;
    this.accessibleAirCarriers = employee.accessibleAirCarriers;
  }

  get emailAddress(): string {
    return this.employee.email;
  }

  get firstName(): string {
    return this.employee.firstName;
  }

  get lastName(): string {
    return this.employee.lastName;
  }

  get fullName(): string {
    return `${this.employee.firstName} ${this.employee.lastName}`;
  }

  get id(): string {
    return this.employee.id;
  }

  get isAviationSupervisor(): boolean {
    return this.employee.isAviationSupervisor;
  }

  get isPilot(): boolean {
    return this.isPilotInCommand || this.isCoPilot;
  }

  get isCoPilot(): boolean {
    return this.employee.role === 'CoPilot';
  }

  get isPilotInCommand(): boolean {
    return this.employee.role === 'PilotInCommand';
  }

  get isPrdUser(): boolean {
    return (
      this.employee.userRoles.includes(UserRole.PrdUser) ||
      this.employee.userRoles.includes(UserRole.PrdAdmin)
    );
  }

  get isFlightLogUser(): boolean {
    return (
      this.isFlightLogViewer ||
      this.isPilot ||
      this.employee.isAviationSupervisor ||
      this.employee.userRoles.includes(UserRole.Pilot)
    );
  }

  get isFlightLogViewer(): boolean {
    return this.employee.userRoles.includes(UserRole.FlightLogViewer);
  }
}

export class UserViewModel extends EmployeeViewModel {
  // public readonly identity: Identity;
  public readonly employee: EmployeeEntity;
  public readonly company: CompanyEntity;
  public readonly flightLogFeatures: FlightLogFeatures;
  public readonly legacy: UserProps_Deprecated;
  public readonly latestShift$: Observable<DutyLogEntryViewModel | undefined>;
  public readonly previousShift$: Observable<DutyLogEntryViewModel | undefined>;
  public readonly base?: BaseEntity;
  private _onDestroy$ = new Subject();

  public get canImpersonate(): boolean {
    return this.userRoles?.includes(UserRole.Developer);
  }

  get isOnDuty$(): Observable<boolean> {
    return this.latestShift$.pipe(
      map((currentShift) => {
        if (currentShift != null) {
          const { dutyPeriodEndTime: endTime, dutyPeriodStartTime: startTime } = currentShift;

          if (startTime == null && endTime == null) {
            return false;
          }
          return endTime == null;
        }
        return false;
      }),

      distinctUntilChanged(),
    );
  }

  constructor(
    employee: EmployeeEntity,
    company: CompanyEntity,
    base: BaseEntity | undefined,
    private _store: Store<AppState>,
  ) {
    super(employee);
    this.base = base;
    this.company = company;
    // this.identity = identity;
    this.latestShift$ = _store.select(selectShiftsByStartTimeDesc(employee.id)).pipe(
      map((shifts) => shifts[0]),
      map((dl) => (dl != null ? new DutyLogEntryViewModel(dl) : undefined)),
      distinctUntilChanged(),
      takeUntil(this._onDestroy$),
    );
    this.previousShift$ = _store.select(selectShiftsByStartTimeDesc(employee.id)).pipe(
      map((shifts) => (shifts[0]?.dutyPeriodEndTime == null ? shifts[1] : shifts[0])),
      map((dl) => (dl != null ? new DutyLogEntryViewModel(dl) : undefined)),
      distinctUntilChanged(),
      takeUntil(this._onDestroy$),
    );

    this.legacy = {
      flightLogFeatures: this.flightLogFeatures,
      authenticated: true,
      companycode: this.companycode,
      department: undefined,
      // tslint:disable-next-line: no-non-null-assertion
      emailaddress: this.email,
      employee: this.employee,
      // tslint:disable-next-line: no-non-null-assertion
      employee_id: this.employee.employeeId!,
      firstname: this.employee.firstName,
      impersonatedBy: 'TODO',
      isAviationSupervisor: this.isAviationSupervisor,
      isPilot: this.isPilot,
      lastname: this.employee.lastName,
      roles: this.userRoles,
      // tslint:disable-next-line: no-non-null-assertion
      username: this.email,
      viewModel: this,
    };
    this.flightLogFeatures = new FlightLogFeatures(this);
  }

  public dispose() {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  public toJSON(): any {
    return {
      ...this,
      legacy: {
        ...this.legacy,
        viewModel: undefined,
      },
      flightLogFeatures: {
        ...this.flightLogFeatures,
        _user: undefined,
      },
      latestShift$: undefined,
      previousShift$: undefined,
      isOnDuty$: undefined,
      _store: undefined,
    };
  }
}
